import type { CityConfig } from "../city-template/types";

export const zanesvilleConfig: CityConfig = {
  city: "Zanesville",
  state: "Ohio",
  stateAbbr: "OH",
  slug: "zanesville",
  route: "/cities/zanesville",
  title: "Emergency Passport Zanesville, OH | Expedited Passport Services | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Zanesville, OH — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Muskingum County and the surrounding area including Cambridge, New Concord, and Dresden. Get your passport fast!",
  h1: "Emergency & Expedited Passport Services in Zanesville, OH",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Zanesville and Muskingum County travelers — including the Y-Bridge region — get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Zanesville%20Ohio%20Y-Bridge%20over%20the%20confluence%20of%20the%20Licking%20and%20Muskingum%20Rivers%20with%20historic%20downtown%20buildings%20along%20the%20riverfront%2C%20warm%20afternoon%20light%20illuminating%20the%20iconic%20bridge%20and%20brick%20architecture%2C%20scenic%20view%20of%20the%20Ohio%20river%20valley%20with%20tree-lined%20banks%2C%20American%20small%20town%20atmosphere%20with%20rich%20industrial%20heritage%2C%20professional%20photography%20with%20warm%20earthy%20tones&width=1800&height=700&seq=zanesville-hero-oh-001&orientation=landscape",
  heroImageAlt: "Zanesville Ohio Y-Bridge over the Muskingum and Licking Rivers at golden hour",
  geoPosition: "39.9405;-82.0132",
  geoPlacename: "Zanesville, Ohio",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "230 S Dearborn St, Suite 380, Chicago, IL 60604",
    distance: "Located in downtown Chicago — approximately 360 miles from Zanesville via I-70",
  },
  nearbyCitiesZips: [
    { name: "Zanesville", zips: "43701, 43702" },
    { name: "Cambridge", zips: "43725" },
    { name: "New Concord", zips: "43762" },
    { name: "Dresden", zips: "43821" },
    { name: "Newark", zips: "43055, 43058, 43093" },
    { name: "Coshocton", zips: "43812" },
    { name: "Philo", zips: "43771" },
    { name: "Roseville", zips: "43777" },
    { name: "Adamsville", zips: "43802" },
    { name: "Duncan Falls", zips: "43734" },
    { name: "East Fultonham", zips: "43735" },
    { name: "South Zanesville", zips: "43701" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Zanesville departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-global-line", title: "Y-Bridge Region Coverage", desc: "Serving Muskingum County and the surrounding Appalachian foothills" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Columbus, OH", route: "/cities/columbus-oh" },
    { name: "Fast passport renewal in Newark, OH", route: "/cities/newark-oh" },
    { name: "Emergency passport help in Lancaster, OH", route: "/cities/lancaster-oh" },
    { name: "Urgent passport services in Marion, OH", route: "/cities/marion-oh" },
    { name: "Same-day passport help in Athens, OH", route: "/cities/athens-oh" },
    { name: "Passport courier services in Cleveland, OH", route: "/cities/cleveland" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Zanesville remotely via secure courier. We do not have a walk-in office in Zanesville — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Muskingum County and the surrounding Y-Bridge region.",
  fpcOfficeDistance: "Serving all of Muskingum County and the surrounding Appalachian foothills",
  serviceAreaIntro: "From downtown Zanesville near the iconic Y-Bridge to the rolling Appalachian foothills of Muskingum County, from Cambridge and New Concord to Dresden and Coshocton — Fast Passport Center serves the entire region. We regularly help travelers from the rural communities throughout eastern Ohio get their passports fast.",
  nearbyCitiesIntro: "We serve all Zanesville neighborhoods and the surrounding Muskingum County region including Cambridge, New Concord, Dresden, Newark, Coshocton, Philo, Roseville, Adamsville, Duncan Falls, East Fultonham, and South Zanesville. If you are anywhere in the Zanesville area or eastern Ohio, we have you covered.",
  howItWorksIntro: "Most Zanesville travelers complete everything online and ship documents overnight. No need to drive to Chicago or wait in line at the regional agency.",
  mistakesIntro: "Zanesville travelers often face long drives to the nearest regional passport agency. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Zanesville and Muskingum County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Zanesville? Here are the most common ones we hear from Muskingum County travelers.",
  statePageRoute: "/states/ohio",
  statePageName: "Ohio",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Zanesville international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Karen L.",
      location: "Zanesville, OH",
      rating: 5,
      text: "My daughter's passport expired two weeks before her study abroad program in Spain. Fast Passport Center had her renewal ready in 4 days. I couldn't believe how smooth the process was.",
      service: "Passport Renewal",
    },
    {
      name: "Tom H.",
      location: "Cambridge, OH",
      rating: 5,
      text: "I thought I'd have to drive to Columbus or Cleveland for an emergency passport. Fast Passport Center handled everything remotely from right here in Guernsey County. Got mine in 3 days.",
      service: "Emergency Passport",
    },
    {
      name: "Jessica R.",
      location: "New Concord, OH",
      rating: 5,
      text: "As a Muskingum College student planning a semester abroad, I needed a fast passport. They walked me through every step and had it ready in 5 days. Great service for college students.",
      service: "New Passport",
    },
    {
      name: "Robert D.",
      location: "Dresden, OH",
      rating: 5,
      text: "Business trip to Japan came up with only 5 days notice. They got me a passport in 4 business days. Professional team, great communication, and I never had to leave the Zanesville area.",
      service: "Emergency Passport",
    },
  ],
  facilities: [
    {
      id: "zanesville-1",
      name: "Zanesville Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "405 Market St",
      city: "Zanesville",
      state: "OH",
      zip: "43701",
      phone: "(740) 452-4511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Downtown Zanesville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Main downtown facility. High volume — appointments strongly recommended. Serves all of Muskingum County.",
      icon: "ri-mail-line",
    },
    {
      id: "zanesville-2",
      name: "Cambridge Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "650 Wheeling Ave",
      city: "Cambridge",
      state: "OH",
      zip: "43725",
      phone: "(740) 439-4441",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Cambridge, 25 miles east of Zanesville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Guernsey County residents. Less crowded than Zanesville. Call for appointment.",
      icon: "ri-mail-line",
    },
    {
      id: "zanesville-3",
      name: "New Concord Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "59 W Main St",
      city: "New Concord",
      state: "OH",
      zip: "43762",
      phone: "(740) 826-7211",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "New Concord, 10 miles east of Zanesville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Muskingum College area and eastern Muskingum County. Smaller facility — call ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "zanesville-4",
      name: "Coshocton Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "120 Chestnut St",
      city: "Coshocton",
      state: "OH",
      zip: "43812",
      phone: "(740) 622-2151",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Coshocton, 22 miles northwest of Zanesville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Coshocton County and northern Muskingum County. Rural location with limited hours.",
      icon: "ri-mail-line",
    },
    {
      id: "zanesville-5",
      name: "Muskingum County Clerk of Courts",
      type: "Court Clerk Passport Acceptance",
      address: "401 Main St",
      city: "Zanesville",
      state: "OH",
      zip: "43701",
      phone: "(740) 455-7104",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "Muskingum County Courthouse",
      website: "https://www.muskingumcounty.org/",
      websiteLabel: "Muskingum County Government",
      notes: "Accepts passport applications. Good alternative to post office with extended hours and appointment availability.",
      icon: "ri-government-line",
    },
    {
      id: "zanesville-6",
      name: "Newark Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "120 W Church St",
      city: "Newark",
      state: "OH",
      zip: "43055",
      phone: "(740) 349-4311",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Newark, 30 miles west of Zanesville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves western Muskingum County and Licking County residents. Ample parking. Appointments required.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 6 hour drive to Chicago", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Zanesville photo shops often use generic sizes. Passport photos must be exactly 2x2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Muskingum County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Zanesville applicants show up with incorrect payment and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Muskingum County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Zanesville travelers often assume standard processing will be fine. It takes 8-11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Zanesville." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Zanesville address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Zanesville before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Zanesville for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24-48 hour turnaround — without the long drive to Chicago." },
    { q: "How fast can I renew my passport in Zanesville?", a: "Fast Passport Center offers fast passport renewal in Zanesville in as little as 24-48 hours through our emergency courier service. For less urgent travel, our 3-5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "I live in Cambridge or New Concord — can you still help me?", a: "Absolutely. Fast Passport Center serves all of Muskingum County and the surrounding eastern Ohio region including Cambridge, New Concord, Dresden, Coshocton, and Philo. Our courier service works seamlessly from any address in the region." },
    { q: "Can I get a same-day passport appointment near Zanesville?", a: "Same-day passport appointments are only available at the Chicago Regional Passport Agency — approximately 6 hours from Zanesville — for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24-48 hour emergency courier processing is the fastest guaranteed option for Muskingum County residents." },
    { q: "Can I expedite a child passport in Zanesville?", a: "Yes. Fast Passport Center expedites child passports in Zanesville for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk Muskingum County families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Zanesville with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24-48 hours." },
    { q: "What is the closest passport agency to Zanesville?", a: "The nearest regional passport agency to Zanesville is the Chicago Passport Agency, approximately 360 miles away via I-70. For urgent processing without the long drive, Fast Passport Center's registered courier service achieves 24-48 hour turnaround from Zanesville without any in-person agency visit required." },
    { q: "Do I need to drive to the Chicago Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Zanesville", address: "2855 Maysville Pike, Zanesville, OH 43701", hours: "Mon-Sun: 8AM-10PM" },
    { name: "Walgreens — Zanesville", address: "2535 Maple Ave, Zanesville, OH 43701", hours: "Mon-Sun: 7AM-11PM" },
    { name: "Walmart Supercenter — Zanesville", address: "2850 Maysville Pike, Zanesville, OH 43701", hours: "Mon-Sun: 6AM-11PM" },
    { name: "CVS Pharmacy — Cambridge", address: "615 Wheeling Ave, Cambridge, OH 43725", hours: "Mon-Sun: 8AM-10PM" },
    { name: "Walgreens — Cambridge", address: "814 Southgate Pkwy, Cambridge, OH 43725", hours: "Mon-Sun: 7AM-11PM" },
    { name: "CVS Pharmacy — New Concord", address: "59 W Main St, New Concord, OH 43762", hours: "Mon-Sun: 8AM-10PM" },
    { name: "Walmart — Coshocton", address: "23800 OH-83, Coshocton, OH 43812", hours: "Mon-Sun: 6AM-11PM" },
    { name: "CVS Pharmacy — Newark", address: "120 W Church St, Newark, OH 43055", hours: "Mon-Sun: 8AM-10PM" },
  ],
  dmvLocations: [
    {
      name: "Muskingum County BMV — Zanesville",
      address: "3314 Maple Ave, Zanesville, OH 43701",
      hours: "Mon-Fri: 8AM-5PM",
      note: "Driver's license and REAL ID services. Appointment strongly recommended.",
      featured: true,
    },
    {
      name: "Guernsey County BMV — Cambridge",
      address: "1001 Campbell Ave, Cambridge, OH 43725",
      hours: "Mon-Fri: 8AM-4:30PM",
      note: "Alternative for Guernsey County residents. REAL ID available.",
      featured: false,
    },
    {
      name: "Coshocton County BMV",
      address: "25051 OH-83, Coshocton, OH 43812",
      hours: "Mon-Fri: 8AM-4:30PM",
      note: "Serves Coshocton County and northern Muskingum County.",
      featured: false,
    },
    {
      name: "AAA — Zanesville",
      address: "3925 Maple Ave, Zanesville, OH 43701",
      hours: "Mon-Fri: 8AM-5:30PM, Sat: 9AM-12PM",
      note: "AAA members can renew licenses and access passport photo services at this location.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "CMH",
      name: "John Glenn Columbus International Airport",
      distance: "55 miles southwest of Zanesville",
      address: "4600 International Gateway, Columbus, OH 43219",
      note: "Closest major airport to Zanesville. Direct flights to major hubs including Atlanta, Chicago, and Charlotte. Best option for Muskingum County travelers.",
      flightUrl: "https://www.flycmh.com/",
      flightLabel: "Columbus Airport",
      featured: true,
    },
    {
      code: "CLE",
      name: "Cleveland Hopkins International Airport",
      distance: "110 miles north of Zanesville",
      address: "5300 Riverside Dr, Cleveland, OH 44135",
      note: "Major international hub with extensive domestic and international routes. Alternative for long-haul international travel.",
      flightUrl: "https://www.clevelandairport.com/",
      flightLabel: "Cleveland Airport",
      featured: false,
    },
    {
      code: "PIT",
      name: "Pittsburgh International Airport",
      distance: "120 miles east of Zanesville",
      address: "1000 Airport Blvd, Pittsburgh, PA 15231",
      note: "Alternative for eastern Ohio residents. Major hub with international connections.",
      flightUrl: "https://www.flypittsburgh.com/",
      flightLabel: "Pittsburgh Airport",
      featured: false,
    },
  ],
};